import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminApi, BACKEND_URL } from '../../api';
import { useApi } from '../../hooks/useApi';
import { Spinner, EmptyState } from '../../components/StateHelpers';
import { useToast } from '../../hooks/use-toast';
import { Plus, Search, Trash2, Edit3, Save, X, Database, ArrowLeft, Eye, Code, FileText, ClipboardCheck, Eraser, Send, Mail } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const LABELS = {
  products: 'Ürünler', categories: 'Kategoriler', servers: 'Sunucular', test_servers: 'Test Sunucuları', test_accounts: 'Test Hesapları',
  faqs: 'SSS', free_products: 'Ücretsiz Ürünler', project_updates: 'Güncellemeler',
  blog_categories: 'Blog Kategorileri',
  projects: 'Projeler', pages: 'Sayfalar', blog_posts: 'Blog Yazıları', menus: 'Menüler',
  users: 'Kullanıcılar', orders: 'Siparişler', coupons: 'Kuponlar', contact_messages: 'İletişim Mesajları',
  mailing_list: 'Bülten Aboneleri', languages: 'Diller', email_templates: 'E-posta Şablonları',
  translations: 'Metin Çevirileri', testimonials: 'Müşteri Yorumları', references: 'Referanslarımız',
  mobile_apps: 'Mobil Uygulamalar', mobile_app_translations: 'Mobil Uygulama Çevirileri',
  galleries: 'Galeri', gallery: 'Galeri', gallery_translations: 'Galeri Çevirileri',
  product_translations: 'Ürün Çevirileri', server_translations: 'Sunucu Çevirileri',
  category_translations: 'Kategori Çevirileri', faq_translations: 'SSS Çevirileri',
  page_translations: 'Sayfa Çevirileri', free_product_translations: 'Ücretsiz Ürün Çevirileri',
  project_translations: 'Proje Çevirileri', project_update_translations: 'Güncelleme Çevirileri',
  blog_translations: 'Blog Çevirileri', blog_category_translations: 'Blog Kategori Çevirileri',
  email_template_translations: 'E-posta Çevirileri',
};

const FIELD_LABELS = {
  id: 'ID', slug: 'Kalıcı Bağlantı (Slug)', price: 'Fiyat', stock: 'Stok', 
  is_featured: 'Öne Çıkan', is_new: 'Yeni Ürün', is_discounted: 'İndirimde Mi', 
  is_not_for_sale: 'Satışa Kapalı', allow_quantity: 'Çoklu Alım İzni',
  category_id: 'Kategori ID', created_at: 'Oluşturulma Tarihi', updated_at: 'Güncellenme Tarihi',
  image: 'Görsel', type: 'Tür', title: 'Başlık', price_monthly: 'Aylık Fiyat', 
  price_yearly: 'Yıllık Fiyat', sort_order: 'Sıralama', is_active: 'Aktif Mi',
  project_id: 'Proje ID', version: 'Versiyon', email: 'E-posta', username: 'Kullanıcı Adı',
  name: 'İsim / Başlık', subject: 'Konu', read: 'Okundu Mu', status: 'Durum',
  parent_id: 'Üst Menü ID', url: 'URL (Bağlantı)', code: 'Kod', flag: 'Bayrak',
  discount: 'İndirim', usage_count: 'Kullanım Sayısı', user_id: 'Kullanıcı ID', total: 'Toplam Tutar',
  template_id: 'Şablon ID', lang: 'Dil Kodu', language_code: 'Dil Kodu (tr/en)', key: 'Anahtar (Key)', value: 'Değer (Çeviri Metni)',
  old_price: 'Eski Fiyat', preview_model: 'Önizleme Modeli (3D/Link)', sales_count: 'Satış Sayısı',
  show_sales_count: 'Satış Sayısını Göster', show_stock: 'Stok Göster', content: 'İçerik / HTML',
  description: 'Açıklama', html: 'HTML Şablonu', description_html: 'HTML Açıklama', short_description: 'Kısa Açıklama',
  meta_description: 'Meta Açıklaması', meta_keywords: 'Meta Anahtar Kelimeleri', meta_title: 'Meta Başlığı',
  role: 'Unvan / Rol (Örn: Server Kurucusu)', rating: 'Puan (1-5 Yıldız)', avatar: 'Profil Resmi / Avatar', logo: 'Logo Görseli', text: 'Yorum Metni',
  play_store_url: 'Google Play Store Linki', app_store_url: 'App Store Linki',
  isim: 'İsim',
  db_host: 'Oyun MySQL Sunucu IP / Host (127.0.0.1)',
  db_port: 'MySQL Port (Default: 3306)',
  db_user: 'MySQL Kullanıcı Adı (Default: root)',
  db_password: 'MySQL Şifresi (Gizli)',
  db_name: 'MySQL Veritabanı Adı (Default: account)',
  hash_method: 'Şifreleme Metodu (MYSQL_PASSWORD / SHA1 / MD5 / SHA256 / PLAIN)',
  buy_url: 'Satın Alma / Ürün Sayfası Linki (Örn: /products?category=server-files)'
};

function fmtField(k) {
  return FIELD_LABELS[k] || k.replace(/_/g, ' ');
}

const slugify = (str) => {
  if (!str) return '';
  const trMap = { 'ş': 's', 'Ş': 's', 'ç': 'c', 'Ç': 'c', 'ğ': 'g', 'Ğ': 'g', 'ü': 'u', 'Ü': 'u', 'ö': 'o', 'Ö': 'o', 'ı': 'i', 'İ': 'i' };
  return str
    .split('')
    .map(c => trMap[c] || c)
    .join('')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};


const PREFERRED_COLS = {
  products: ['id', 'slug', 'price', 'stock', 'is_featured', 'is_new', 'is_discounted', 'created_at'],
  servers: ['id', 'type', 'title', 'slug', 'price_monthly', 'price_yearly'],
  test_servers: ['id', 'name', 'server_type', 'status', 'buy_url', 'sort_order', 'is_active'],
  test_accounts: ['id', 'server_name', 'username', 'email', 'ip', 'created_at'],
  faqs: ['id', 'sort_order', 'is_active', 'created_at'],
  project_updates: ['id', 'project_id', 'title', 'version', 'created_at'],
  users: ['id', 'email', 'username', 'created_at'],
  contact_messages: ['id', 'name', 'email', 'subject', 'created_at', 'read'],
  mailing_list: ['email', 'name', 'created_at'],
  categories: ['id', 'slug', 'image', 'created_at'],
  free_products: ['id', 'slug', 'is_featured', 'created_at'],
  pages: ['id', 'slug', 'title', 'status', 'created_at'],
  menus: ['id', 'parent_id', 'title', 'url', 'sort_order'],
  languages: ['code', 'name', 'flag', 'is_active'],
  translations: ['id', 'key', 'language_code', 'value'],
  testimonials: ['id', 'name', 'role', 'text', 'rating'],
  references: ['id', 'name', 'title', 'logo', 'url', 'sort_order'],
  mobile_apps: ['id', 'image', 'title', 'play_store_url', 'app_store_url', 'sort_order'],
  galleries: ['id', 'image', 'title', 'category', 'sort_order'],
  gallery: ['id', 'image', 'title', 'category', 'sort_order'],
  email_templates: ['id', 'name', 'subject'],
  email_template_translations: ['id', 'template_id', 'lang', 'subject'],
  coupons: ['id', 'code', 'discount', 'usage_count'],
  orders: ['id', 'user_id', 'total', 'status', 'created_at'],
  blog_posts: ['id', 'slug', 'category_id', 'status', 'created_at'],
  blog_categories: ['id', 'slug', 'sort_order'],
};

const SCHEMAS = {
  translations: { id: '', key: '', language_code: 'en', value: '' },
  testimonials: { id: '', name: '', role: '', text: '', avatar: '', rating: 5 },
  references: { id: '', name: '', title: '', logo: '', url: '', sort_order: 0 },
  mobile_apps: { id: '', image: '', title: '', description: '', play_store_url: '', app_store_url: '', sort_order: 0 },
  mobile_app_translations: { id: '', mobile_app_id: '', language_code: 'tr', title: '', description: '' },
  galleries: { id: '', image: '', title: '', category: 'systems', description: '', sort_order: 0 },
  gallery: { id: '', image: '', title: '', category: 'systems', description: '', sort_order: 0 },
  gallery_translations: { id: '', gallery_id: '', language_code: 'tr', title: '', description: '' },
  products: { id: '', slug: '', category_id: '', image: '', price: 0, old_price: 0, stock: 0, is_featured: false, is_new: false, is_discounted: false, is_not_for_sale: false, allow_quantity: false, preview_model: '', sales_count: 0, show_sales_count: false, show_stock: false, download_type: 'file', download_link: '', download_file: '' },
  product_translations: { id: '', product_id: '', language_code: 'tr', title: '', description: '', description_html: '' },
  categories: { id: '', slug: '', image: '', sort_order: 0 },
  category_translations: { id: '', category_id: '', language_code: 'tr', name: '', description: '', meta_title: '', meta_description: '', meta_keywords: '' },
  servers: { id: '', type: 'VDS', slug: '', image: '', price_monthly: 0, price_yearly: 0, sort_order: 0 },
  test_servers: { id: '', name: '', server_type: '1-99 Hard', status: 'active', description: '', buy_url: '/products?category=server-files', image: '', db_host: '127.0.0.1', db_port: 3306, db_user: 'root', db_password: '', db_name: 'account', hash_method: 'MYSQL_PASSWORD', sort_order: 1, is_active: true },
  server_translations: { id: '', server_id: '', language_code: 'tr', title: '', description: '', description_html: '' },
  blog_posts: { id: '', slug: '', category_id: '', image: '', status: 'published' },
  blog_translations: { id: '', post_id: '', language_code: 'tr', title: '', content: '' },
  blog_categories: { id: '', slug: '', sort_order: 0 },
  blog_category_translations: { id: '', category_id: '', language_code: 'tr', title: '' },
  pages: { id: '', slug: '', status: 'published' },
  page_translations: { id: '', page_id: '', language_code: 'tr', title: '', content: '' },
  free_products: { id: '', slug: '', image: '', is_featured: false, download_type: 'file', download_link: '', download_file: '' },
  free_product_translations: { id: '', free_product_id: '', language_code: 'tr', title: '', description: '', content: '' },
  projects: { id: '', slug: '', image: '', link: '', sort_order: 0 },
  project_translations: { id: '', project_id: '', language_code: 'tr', title: '', description: '', content: '' },
  project_updates: { id: '', project_id: '', version: '', created_at: '' },
  project_update_translations: { id: '', project_update_id: '', language_code: 'tr', title: '', content: '' },
  faqs: { id: '', sort_order: 0, is_active: true },
  faq_translations: { id: '', faq_id: '', language_code: 'tr', question: '', answer: '' },
  email_templates: { id: '', name: '', subject: '' },
  email_template_translations: { id: '', template_id: '', lang: 'tr', subject: '', html: '' },
  menus: { id: '', parent_id: '', url: '', sort_order: 0 },
  menu_translations: { id: '', menu_id: '', language_code: 'tr', title: '' },
  languages: { code: 'en', name: 'English', flag: '', is_active: true },
  coupons: { id: '', code: '', discount: 0, usage_count: 0 },
};

function fmtCell(v) {
  if (v === null || v === undefined) return <span className="text-white/25">—</span>;
  if (typeof v === 'boolean' || v === 0 || v === 1) return <span className={v ? 'text-emerald-400' : 'text-white/40'}>{v ? '✓' : '✖'}</span>;
  if (v === 'published' || v === 'active') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">✓ Yayında</span>;
  if (v === 'draft') return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">✎ Taslak</span>;
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + '…' : s;
}

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
    ['link', 'image', 'video'],
    ['clean']
  ],
};

const cleanAllStylesAndDirtyHtml = (htmlStr) => {
  if (!htmlStr) return '';
  return htmlStr
    .replace(/\s*style="[^"]*"/gi, '')
    .replace(/\s*style='[^']*'/gi, '')
    .replace(/\s*class="[^"]*"/gi, '')
    .replace(/\s*class='[^']*'/gi, '')
    .replace(/<\/?span[^>]*>/gi, '')
    .replace(/<\/?font[^>]*>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/<(ol|ul|p)>\s*<\/\1>/gi, '');
};

function RichTextEditorWithCodeView({ value, onChange }) {
  const [codeMode, setCodeMode] = useState(false);
  const [plainTextPaste, setPlainTextPaste] = useState(true);
  const quillRef = React.useRef(null);

  React.useEffect(() => {
    if (!quillRef.current) return;
    const editor = typeof quillRef.current.getEditor === 'function' ? quillRef.current.getEditor() : null;
    if (!editor) return;

    const handleQuillPaste = (e) => {
      if (plainTextPaste) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const text = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
        const cleanText = text.replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ');
        const range = editor.getSelection() || { index: editor.getLength(), length: 0 };
        
        const formatted = cleanText
          .split(/\r?\n/)
          .map(line => line.trim() ? line.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '')
          .join('<br>');

        editor.clipboard.dangerouslyPasteHTML(range.index, formatted, 'user');
      }
    };

    const rootEl = editor.root;
    if (rootEl) {
      rootEl.addEventListener('paste', handleQuillPaste, true);
      return () => {
        rootEl.removeEventListener('paste', handleQuillPaste, true);
      };
    }
  }, [plainTextPaste]);

  const handleCleanNow = () => {
    const cleaned = cleanAllStylesAndDirtyHtml(value);
    onChange(cleaned);
  };

  const handleTextareaPaste = (e) => {
    if (plainTextPaste) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
      const target = e.target;
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || 0;
      const currentVal = value || '';
      const newVal = currentVal.substring(0, start) + text + currentVal.substring(end);
      onChange(newVal);
    }
  };

  return (
    <div className="mt-2 text-white bg-white/5 border border-white/10 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-[#121218] border-b border-white/10 text-xs font-bold">
        <span className="text-white/60 flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-[#e4b44e]" />
          Zengin İçerik Editörü
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleCleanNow}
            title="İçerikteki tüm arkaplan renklerini, oklab/span stillerini ve bozuk etiketleri anında temizler"
            className="px-2.5 py-1 rounded text-[11px] font-semibold flex items-center gap-1.5 bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 transition"
          >
            <Eraser className="w-3.5 h-3.5" />
            Stilleri & Kodları Temizle
          </button>
          <button
            type="button"
            onClick={() => setPlainTextPaste(!plainTextPaste)}
            title="Kopyalanan harici stilleri ve biçimleri temizleyip düz metin olarak yapıştırır"
            className={`px-2.5 py-1 rounded text-[11px] font-semibold flex items-center gap-1.5 transition ${
              plainTextPaste
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                : 'bg-white/5 text-white/50 border border-white/10 hover:text-white'
            }`}
          >
            <ClipboardCheck className="w-3.5 h-3.5" />
            {plainTextPaste ? 'Düz Metin Yapıştırma: Aktif' : 'Düz Metin Yapıştırma: Pasif'}
          </button>
          <button
            type="button"
            onClick={() => {
              if (codeMode) {
                onChange(cleanAllStylesAndDirtyHtml(value));
              }
              setCodeMode(!codeMode);
            }}
            className={`px-2.5 py-1 rounded text-[11px] font-semibold flex items-center gap-1.5 transition ${
              codeMode
                ? 'bg-[#e4b44e] text-black font-bold shadow-lg shadow-[#e4b44e]/20'
                : 'bg-white/10 text-white/80 hover:text-white hover:bg-white/20 border border-white/10'
            }`}
          >
            <Code className="w-3.5 h-3.5" />
            {codeMode ? 'Görsel Editöre Dön' : 'HTML Kodunu Görüntüle / Düzenle'}
          </button>
        </div>
      </div>
      {codeMode ? (
        <div className="relative">
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handleTextareaPaste}
            rows={14}
            className="w-full p-4 bg-[#08080c] text-amber-300 font-mono text-xs border-none focus:outline-none resize-y leading-relaxed"
            placeholder="<h1>HTML kodlarınızı buraya yazabilir veya doğrudan düzenleyebilirsiniz...</h1>"
          />
          <div className="absolute right-3 bottom-3 text-[10px] text-white/30 font-mono pointer-events-none">
            HTML KOD MODU AKTİF
          </div>
        </div>
      ) : (
        <div className="quill-dark">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            modules={quillModules}
            value={value || ''}
            onChange={(content) => onChange(content)}
            className="border-none"
          />
        </div>
      )}
    </div>
  );
}

function EmailPreviewModal({ template, siteLogo, onClose }) {
  if (!template) return null;

  let html = template.html || template.content || '';
  let subject = template.subject || '';

  if (template.translations) {
    const tr = template.translations.tr || template.translations.en || Object.values(template.translations)[0];
    if (tr) {
      if (tr.html) html = tr.html;
      else if (tr.content) html = tr.content;
      if (tr.subject) subject = tr.subject;
    }
  }

  const sampleData = {
    ticket_id: '10492',
    ticket_subject: 'Kurulum ve Yapılandırma Desteği',
    ticket_message: 'Merhaba, aldığım paketin VDS kurulumunun yapılmasını rica ediyorum.',
    site_title: 'Aegis Studios',
    site_url: 'https://aegistudios.com',
    user_name: 'Ahmet Yılmaz',
    customer_name: 'Ahmet Yılmaz',
    name: 'Ahmet Yılmaz',
    username: 'ahmetyilmaz',
    email: 'musteri@example.com',
    order_id: 'AEG-98421',
    order_date: '01.08.2026',
    order_total: '₺1.250,00',
    product_name: 'Metin2 PvP Server Files v4.2',
    title: 'Metin2 PvP Server Files v4.2',
    version: 'v4.2.1',
    reset_link: 'https://aegistudios.com/reset-password?token=sample123',
    download_link: 'https://aegistudios.com/account/downloads'
  };

  let renderedSubject = subject;
  let renderedInnerHtml = html;

  Object.entries(sampleData).forEach(([key, val]) => {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
    renderedSubject = renderedSubject.replace(regex, val);
    renderedInnerHtml = renderedInnerHtml.replace(regex, val);
  });

  const formatEmailButtons = (contentHtml) => {
    if (!contentHtml) return '';
    const btnStyle = 'display: inline-block; padding: 13px 28px; background: linear-gradient(135deg, #e4b44e 0%, #c89530 100%); color: #08080b !important; font-weight: 800; font-size: 13px; text-decoration: none !important; border-radius: 8px; box-shadow: 0 4px 15px rgba(228, 180, 78, 0.35); text-transform: uppercase; letter-spacing: 0.5px; margin: 12px 0; font-family: sans-serif;';
    return contentHtml.replace(/<a\s+(?:[^>]*?\s+)?href=["']([^"']+)["'][^>]*?>(.*?)<\/a>/gi, (full, href, text) => {
      if (full.includes('display:') || full.includes('color: #e4b44e')) return full;
      return `<a href="${href}" target="_blank" style="${btnStyle}">${text} &rarr;</a>`;
    });
  };

  const styledInnerHtml = formatEmailButtons(renderedInnerHtml);

  const logoFilename = siteLogo || 'site_logo_697de09612208.png';
  const logoUrl = logoFilename.startsWith('http') ? logoFilename : `https://aegistudios.com/assets/img/${logoFilename}`;

  const fullEmailDocument = `
    <!DOCTYPE html>
    <html lang="tr">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${renderedSubject}</title>
        <style>
          body { margin: 0; padding: 0; font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #08080b; color: #ffffff; -webkit-font-smoothing: antialiased; }
        </style>
    </head>
    <body style="margin: 0; padding: 0; font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #08080b; color: #ffffff;">
        <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #08080b; padding: 30px 0;">
            <tr>
                <td align="center">
                    <table width="600" border="0" cellspacing="0" cellpadding="0" style="background-color: #121218; border-radius: 12px; overflow: hidden; border: 1px solid #1a1a24; box-shadow: 0 10px 40px rgba(0,0,0,0.5);">
                        <!-- Header Logo -->
                        <tr>
                            <td align="center" style="background-color: #0d0d12; padding: 30px 40px; border-bottom: 2px solid #e4b44e;">
                                <a href="https://aegistudios.com" target="_blank" style="text-decoration: none; display: block; text-align: center;">
                                  <img src="${logoUrl}" alt="Aegis Studios" style="max-height: 52px; height: auto; max-width: 240px; border: 0; outline: none; display: block; margin: 0 auto 8px auto;" onerror="this.style.display='none';" />
                                  <span style="color: #e4b44e; font-size: 18px; font-weight: 800; letter-spacing: 2px; font-family: 'Segoe UI', Roboto, sans-serif; text-transform: uppercase; text-shadow: 0 2px 10px rgba(228,180,78,0.2);">AEGIS STUDIOS</span>
                                </a>
                            </td>
                        </tr>
                        <!-- Content -->
                        <tr>
                            <td style="padding: 35px 40px; line-height: 1.6; color: #d0d0d5; font-size: 15px;">
                                ${styledInnerHtml || '<p style="color:#888; text-align:center;">Şablon metni boş veya henüz girilmemiş.</p>'}
                            </td>
                        </tr>
                        <!-- Footer -->
                        <tr>
                            <td align="center" style="background-color: #0a0a0e; padding: 25px 40px; font-size: 12px; color: #666677; border-top: 1px solid #1a1a24;">
                                <div style="margin-bottom: 18px; text-align: center;">
                                    <a href="https://discord.gg/h9xnRChVY8" target="_blank" style="display: inline-block; margin: 4px 6px; text-decoration: none; color: #7289da; font-weight: 700; font-size: 12px; padding: 6px 14px; background: rgba(114, 137, 218, 0.12); border: 1px solid rgba(114, 137, 218, 0.3); border-radius: 6px;">🎮 Discord</a>
                                    <a href="https://api.whatsapp.com/send/?phone=905374630644" target="_blank" style="display: inline-block; margin: 4px 6px; text-decoration: none; color: #25d366; font-weight: 700; font-size: 12px; padding: 6px 14px; background: rgba(37, 211, 102, 0.12); border: 1px solid rgba(37, 211, 102, 0.3); border-radius: 6px;">💬 WhatsApp</a>
                                    <a href="https://instagram.com" target="_blank" style="display: inline-block; margin: 4px 6px; text-decoration: none; color: #e1306c; font-weight: 700; font-size: 12px; padding: 6px 14px; background: rgba(225, 48, 108, 0.12); border: 1px solid rgba(225, 48, 108, 0.3); border-radius: 6px;">📸 Instagram</a>
                                    <a href="https://youtube.com" target="_blank" style="display: inline-block; margin: 4px 6px; text-decoration: none; color: #ff0000; font-weight: 700; font-size: 12px; padding: 6px 14px; background: rgba(255, 0, 0, 0.12); border: 1px solid rgba(255, 0, 0, 0.3); border-radius: 6px;">▶ YouTube</a>
                                </div>
                                <div style="margin-bottom: 12px;">
                                    <a href="https://aegistudios.com" style="color: #e4b44e; text-decoration: none; font-weight: 600; margin: 0 10px;">Ana Sayfa</a>
                                    <span style="color: #333344;">•</span>
                                    <a href="https://aegistudios.com/account" style="color: #e4b44e; text-decoration: none; font-weight: 600; margin: 0 10px;">Hesabım</a>
                                    <span style="color: #333344;">•</span>
                                    <a href="https://aegistudios.com/contact" style="color: #e4b44e; text-decoration: none; font-weight: 600; margin: 0 10px;">İletişim</a>
                                </div>
                                <p style="margin: 4px 0; color: #888899;">&copy; 2026 Aegis Studios. Tüm hakları saklıdır.</p>
                                <p style="margin: 4px 0; color: #555566;">Bu e-posta otomatik olarak gönderilmiştir. Lütfen yanıtlamayınız.</p>
                            </td>
                        </tr>
                    </table>
                </td>
            </tr>
        </table>
    </body>
    </html>
  `;

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl h-[92vh] bg-[#0c0c10] border border-white/10 rounded-2xl flex flex-col overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 bg-[#121218] border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#e4b44e]/10 border border-[#e4b44e]/30 flex items-center justify-center text-[#e4b44e]">
              <Eye className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-white">E-posta Şablonu Canlı Önizleme</h2>
              <p className="text-white/50 text-xs">{template.name || template.id || 'Şablon'}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-3 bg-[#08080c] border-b border-white/5 text-xs text-white/70 space-y-1.5 font-mono">
          <div className="flex items-center gap-2">
            <span className="text-white/30 uppercase tracking-wider w-16">Gönderen:</span>
            <span className="text-emerald-400 font-semibold">Aegis Studios &lt;info@aegistudios.com&gt;</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/30 uppercase tracking-wider w-16">Konu:</span>
            <span className="text-amber-300 font-semibold">{renderedSubject || '(Konu Tanımlanmamış)'}</span>
          </div>
        </div>

        <div className="flex-1 bg-[#08080b] p-2 overflow-hidden">
          <iframe
            title="email-preview"
            srcDoc={fullEmailDocument}
            className="w-full h-full border-none rounded-lg bg-[#08080b]"
          />
        </div>
      </div>
    </div>
  );
}

export default function AdminResource() {
  const { collection } = useParams();
  const nav = useNavigate();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activeLang, setActiveLang] = useState('tr');

  const { data, loading, refresh } = useApi(() => adminApi.list(collection), [collection]);
  const { data: langsData } = useApi(() => adminApi.list('languages'), []);
  const { data: catsData } = useApi(() => adminApi.list('categories'), []);
  const { data: projectsData } = useApi(() => adminApi.list('projects'), []);
  const { data: blogCatsData } = useApi(() => adminApi.list('blog_categories'), []);
  const { data: menusData } = useApi(() => adminApi.list('menus'), []);
  const { data: settingsData } = useApi(() => adminApi.list('settings'), []);

  const siteLogo = useMemo(() => settingsData?.find(s => s.setting_key === 'site_logo')?.setting_value, [settingsData]);
  const langs = useMemo(() => langsData?.map(l => l.code) || ['tr', 'en'], [langsData]);

  const rows = data || [];
  const sortedRows = useMemo(() => {
    return (data || []).slice().sort((a, b) => {
      if (a.created_at && b.created_at) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  }, [data]);

  const cols = useMemo(() => {
    if (PREFERRED_COLS[collection]) return PREFERRED_COLS[collection];
    if (!rows.length) return [];
    return Object.keys(rows[0]).filter((k) => k !== '_id' && k !== 'translations').slice(0, 8);
  }, [collection, rows]);

  const TRANSLATIONS_MAP = {
    products: 'product_translations',
    categories: 'category_translations',
    servers: 'server_translations',
    faqs: 'faq_translations',
    project_updates: 'project_update_translations',
    projects: 'project_translations',
    pages: 'page_translations',
    free_products: 'free_product_translations',
    blog_posts: 'blog_translations',
    blog_categories: 'blog_category_translations',
    email_templates: 'email_template_translations',
    menus: 'menu_translations'
  };

  const trCollName = TRANSLATIONS_MAP[collection];
  const hasTranslations = Boolean(trCollName && SCHEMAS[trCollName]);
  const trSchema = hasTranslations ? SCHEMAS[trCollName] : null;

  const [missingOnly, setMissingOnly] = useState(false);

  const filtered = useMemo(() => {
    let res = sortedRows;
    if (q) {
      res = res.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
    }
    if (missingOnly) {
      if (collection === 'translations') {
        res = res.filter((r) => !r.value || String(r.value).trim() === '');
      } else if (hasTranslations) {
        res = res.filter((r) => {
          if (!r.translations) return true;
          return langs.some(l => !r.translations[l] || Object.values(r.translations[l]).some(val => val === '' || val === null || val === undefined));
        });
      }
    }
    return res;
  }, [sortedRows, q, missingOnly, collection, hasTranslations, langs]);

  const openNew = () => {
    const obj = SCHEMAS[collection] ? { ...SCHEMAS[collection] } : {};
    if (!SCHEMAS[collection]) cols.forEach(c => obj[c] = '');
    if (hasTranslations) {
      obj.translations = {};
      langs.forEach(l => {
        obj.translations[l] = { ...trSchema, language_code: l };
        if (trSchema.lang !== undefined) obj.translations[l].lang = l;
      });
    }
    setEditItem(obj);
    setCreating(true);
  };
  
  const openEdit = (row) => {
    const schemaDefaults = SCHEMAS[collection] || {};
    const obj = { ...schemaDefaults, ...row };
    if (hasTranslations) {
      if (!obj.translations) obj.translations = {};
      langs.forEach(l => {
        if (!obj.translations[l]) {
          obj.translations[l] = { ...trSchema, language_code: l };
          if (trSchema.lang !== undefined) obj.translations[l].lang = l;
        }
      });
    }
    setEditItem(obj);
    setCreating(false);
  };

  const handleUpload = async (e, k, isTrans = false) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const isProtected = k === 'download_file';
      const res = isProtected ? await adminApi.uploadProtected(file) : await adminApi.upload(file);
      const val = isProtected ? res.filename : res.url;
      if (val) {
        if (isTrans) {
          setEditItem({
            ...editItem,
            translations: {
              ...editItem.translations,
              [activeLang]: { ...editItem.translations[activeLang], [k]: val }
            }
          });
        } else {
          setEditItem({ ...editItem, [k]: val });
        }
      }
    } catch (err) { toast({ title: 'Yükleme Hatası', description: 'Dosya yüklenemedi', variant: 'destructive' }); }
  };

  const [savingItem, setSavingItem] = useState(false);

  const save = async () => {
    setSavingItem(true);
    try {
      if (creating) {
        await adminApi.create(collection, editItem);
        toast({ title: 'Oluşturuldu' });
      } else {
        const itemId = editItem.id !== undefined && editItem.id !== null && editItem.id !== '' ? editItem.id : (editItem.code ? editItem.code : editItem.email);
        await adminApi.update(collection, itemId, editItem);
        toast({ title: 'Güncellendi' });
      }
      setEditItem(null);
      refresh();
    } catch (e) {
      toast({ title: 'Hata', description: e?.response?.data?.detail || 'Kaydedilemedi', variant: 'destructive' });
    } finally {
      setSavingItem(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    const itemId = row.id !== undefined && row.id !== null && row.id !== '' ? row.id : (row.code ? row.code : row.email);
    try { await adminApi.remove(collection, itemId); toast({ title: 'Silindi' }); refresh(); }
    catch (e) { toast({ title: 'Hata', description: 'Silinemedi', variant: 'destructive' }); }
  };

  const renderField = (k, v, isTrans = false, targetLang = null) => {
    if (collection === 'test_servers' && [
      'online_players', 'rate_drop', 'rate_exp', 'rate_yang',
      'client_download_url', 'max_level', 'registration_url'
    ].includes(k)) {
      return null;
    }

    const isBool = typeof v === 'boolean' || k.startsWith('is_') || k.startsWith('show_') || k === 'allow_quantity';
    const isImage = k === 'image' || k === 'icon' || k === 'avatar' || k === 'logo' || k === 'flag';
    const isFile = k === 'download_file';
    const isTextarea = k === 'description' || k === 'content' || k === 'html' || k === 'answer' || k.includes('description') || k.includes('content') || (typeof v === 'string' && v.length > 100 && !isImage && !isFile);
    
    const updateVal = (val) => {
      const l = targetLang || activeLang;
      if (isTrans) {
        const updatedTrans = {
          ...editItem.translations,
          [l]: { ...editItem.translations[l], [k]: val }
        };
        let newItem = { ...editItem, translations: updatedTrans };
        if ((k === 'title' || k === 'name') && (l === 'tr' || !editItem.slug)) {
          const generated = slugify(val);
          if (generated && (!editItem.slug || editItem._autoSlug)) {
            newItem.slug = generated;
            newItem._autoSlug = true;
          }
        }
        setEditItem(newItem);
      } else {
        let newItem = { ...editItem, [k]: val };
        if (k === 'slug') newItem._autoSlug = false;
        setEditItem(newItem);
      }
    };

    const dType = editItem.download_type || 'file';
    if (k === 'download_link' && dType !== 'link') return null;
    if (k === 'download_file' && dType !== 'file') return null;

    const resolveImgUrl = (val) => {
      if (!val) return '';
      const s = String(val);
      if (s.startsWith('http') || s.startsWith('data:')) return s;
      if (s.startsWith('uploads/')) return `/${s}`;
      if (s.startsWith('/')) return s;
      if (s.startsWith('upload_')) return `/uploads/${s}`;
      if (s.startsWith('prod_gallery_')) return `/assets/img/products/${s}`;
      return `/assets/img/${s}`;
    };

    return (
      <div key={k} className={isTextarea ? "md:col-span-2" : ""}>
        <label className="text-xs font-bold text-white/50 tracking-wider uppercase">
          {collection === 'project_updates' && k === 'project_id' ? 'Kategori (Proje)' : fmtField(k)} {isTrans && `(${activeLang.toUpperCase()})`}
        </label>
        {isBool ? (
          <div className="mt-2">
            <button onClick={() => updateVal(!v)} className={`w-12 h-6 rounded-full transition-colors relative ${v ? 'bg-emerald-500' : 'bg-white/10'}`}>
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${v ? 'left-7' : 'left-1'}`} />
            </button>
          </div>
        ) : isImage || isFile ? (
          <div className="mt-2 flex items-center gap-3">
            {isImage ? (v ? <img src={resolveImgUrl(v)} alt="preview" className="w-12 h-12 object-cover rounded-lg border border-white/10 bg-black" /> : <div className="w-12 h-12 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/20 text-xs">Yok</div>) : null}
            <input value={v === null || v === undefined ? '' : v} onChange={(e) => updateVal(e.target.value)} className="flex-1 h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white" placeholder={isFile ? "Dosya adı (Örn: file.zip)" : "Dosya adı veya resim linki"} />
            <label className="btn-gold h-10 px-4 rounded-lg flex items-center justify-center text-xs font-bold cursor-pointer whitespace-nowrap">
              Dosya Seç
              <input type="file" className="hidden" accept={isImage ? "image/*" : ".zip,.rar,.7z,.tar,.gz,.pdf,.txt"} onChange={(e) => handleUpload(e, k, isTrans)} />
            </label>
          </div>
        ) : isTextarea ? (
          <RichTextEditorWithCodeView value={v || ''} onChange={(content) => updateVal(content)} />
        ) : k === 'category_id' ? (
          <select value={v === null || v === undefined ? '' : v} onChange={(e) => updateVal(e.target.value ? Number(e.target.value) : '')} className="mt-1 w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#e4b44e]/50 transition-colors">
            <option value="" className="bg-[#08080b] text-white">Seçiniz</option>
            {collection === 'products' && (catsData || []).map(c => <option key={c.id} value={c.id} className="bg-[#08080b] text-white">{c.translations?.tr?.name || c.translations?.tr?.title || c.name || c.slug || c.id}</option>)}
            {collection === 'blog_posts' && (blogCatsData || []).map(c => <option key={c.id} value={c.id} className="bg-[#08080b] text-white">{c.translations?.tr?.name || c.translations?.tr?.title || c.name || c.slug || c.id}</option>)}
          </select>
        ) : k === 'parent_id' ? (
          <select value={v === null || v === undefined ? '' : v} onChange={(e) => updateVal(e.target.value ? Number(e.target.value) : '')} className="mt-1 w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#e4b44e]/50 transition-colors">
            <option value="" className="bg-[#08080b] text-white">Yok (Üst Menü)</option>
            {(menusData || []).map(m => <option key={m.id} value={m.id} className="bg-[#08080b] text-white">{m.translations?.tr?.name || m.translations?.tr?.title || m.name || m.url || m.id}</option>)}
          </select>
        ) : k === 'project_id' ? (
          <select value={v === null || v === undefined ? '' : v} onChange={(e) => updateVal(e.target.value ? (isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)) : '')} className="mt-1 w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#e4b44e]/50 transition-colors">
            <option value="" className="bg-[#08080b] text-white">Seçiniz</option>
            {(projectsData || []).map(p => <option key={p.id} value={p.id} className="bg-[#08080b] text-white">{p.translations?.tr?.name || p.translations?.tr?.title || p.name || p.slug || p.id}</option>)}
          </select>
        ) : k === 'download_type' ? (
          <select value={v === null || v === undefined ? 'file' : v} onChange={(e) => updateVal(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#e4b44e]/50 transition-colors">
            <option value="file" className="bg-[#08080b] text-white">Dosya Yükle</option>
            <option value="link" className="bg-[#08080b] text-white">Dış Bağlantı (Link)</option>
          </select>
        ) : k === 'status' ? (
          <select value={v === null || v === undefined ? 'published' : v} onChange={(e) => updateVal(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#e4b44e]/50 transition-colors">
            <option value="published" className="bg-[#08080b] text-emerald-400">✓ Yayında (Aktif - Sitede Görünür)</option>
            <option value="draft" className="bg-[#08080b] text-amber-300">✎ Taslak (Pasif - Sitede Gizli)</option>
          </select>
        ) : (
          <input disabled={(!isTrans && (k === 'id' || k === 'created_at' || k === 'updated_at')) || (isTrans && (k === 'id' || k.endsWith('_id') || k === 'language_code' || k === 'lang'))} value={v === null || v === undefined ? '' : v} onChange={(e) => updateVal(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:border-[#e4b44e]/50 transition-colors" />
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => nav('/admin')} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4" /></button>
        <div className="flex-1">
          <h1 className="font-display text-3xl font-black">{LABELS[collection] || collection}</h1>
          <p className="text-white/50 text-sm">{rows.length.toLocaleString('tr-TR')} kayıt</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara..." className="h-10 pl-9 pr-3 rounded-lg bg-white/5 border border-white/10 text-sm w-64" />
        </div>
        <button
          onClick={() => setMissingOnly(!missingOnly)}
          className={`h-10 px-3 rounded-lg border text-xs font-bold transition whitespace-nowrap ${
            missingOnly ? 'bg-amber-500/20 border-amber-500 text-amber-300' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'
          }`}
        >
          {missingOnly ? '✓ Eksik Çeviriler Filtrelendi' : 'Eksikleri Göster'}
        </button>
        <button onClick={openNew} className="btn-gold h-10 px-4 rounded-lg inline-flex items-center gap-2 text-xs font-extrabold tracking-widest"><Plus className="w-4 h-4" /> EKLE</button>
      </div>

      {loading ? <Spinner /> : rows.length === 0 ? <EmptyState icon={Database} title="Kayıt Yok" /> : (
        <div className="rounded-2xl border border-white/8 bg-[#08080b] overflow-hidden">
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-white/50 text-xs">
                <tr>{cols.map((c) => <th key={c} className="text-left px-4 py-3 font-semibold tracking-wider uppercase">{collection === 'project_updates' && c === 'project_id' ? 'Kategori (Proje)' : fmtField(c)}</th>)}<th className="px-4 py-3">İşlem</th></tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((row, i) => (
                  <tr key={row.id || row.email || i} className="border-t border-white/5 hover:bg-white/[0.02]">
                    {cols.map((c) => {
                      let cellVal = row[c];
                      if ((cellVal === undefined || cellVal === null || cellVal === '') && row.translations) {
                        const tr = row.translations.tr || row.translations.en || Object.values(row.translations)[0];
                        if (tr && tr[c] !== undefined && tr[c] !== null && tr[c] !== '') cellVal = tr[c];
                      }
                      if (c === 'project_id' && projectsData) {
                        const p = projectsData.find(x => x.id === cellVal);
                        if (p) cellVal = p.translations?.tr?.name || p.translations?.tr?.title || p.name || p.slug || p.id;
                      }
                      if (c === 'category_id') {
                        const arr = collection === 'products' ? catsData : collection === 'blog_posts' ? blogCatsData : null;
                        if (arr) {
                          const p = arr.find(x => x.id === cellVal);
                          if (p) cellVal = p.translations?.tr?.title || p.translations?.tr?.name || p.name || p.slug || p.id;
                        }
                      }
                      if (c === 'parent_id' && menusData) {
                        const p = menusData.find(x => x.id === cellVal);
                        if (p) cellVal = p.translations?.tr?.name || p.translations?.tr?.title || p.name || p.url || p.id;
                      }
                      if ((c === 'image' || c === 'icon' || c === 'avatar' || c === 'logo' || c === 'flag') && cellVal) {
                        const imgUrl = String(cellVal).startsWith('http') || String(cellVal).startsWith('data:') ? cellVal : String(cellVal).startsWith('uploads/') ? `/${cellVal}` : String(cellVal).startsWith('/') ? cellVal : String(cellVal).startsWith('upload_') ? `/uploads/${cellVal}` : String(cellVal).startsWith('prod_gallery_') ? `/assets/img/products/${cellVal}` : `/assets/img/${cellVal}`;
                        return (
                          <td key={c} className="px-4 py-2">
                            <img src={imgUrl} alt="thumb" className="w-10 h-10 object-cover rounded-lg border border-white/10 bg-black/40" />
                          </td>
                        );
                      }
                      return <td key={c} className="px-4 py-3 text-white/80">{fmtCell(cellVal)}</td>;
                    })}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {collection === 'project_updates' && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`'${row.version || row.id}' güncellemesini TÜM KAYITLI KULLANICILARA e-posta bildirimi olarak göndermek istiyor musunuz?`)) return;
                              try {
                                const res = await adminApi.sendUpdateNotification(row.id);
                                toast({ title: 'Başarılı', description: res?.message || 'Bildirim e-postaları gönderildi.' });
                              } catch (err) {
                                toast({ title: 'Hata', description: err.response?.data?.detail || 'Mail gönderilemedi.', variant: 'destructive' });
                              }
                            }}
                            title="Bu Güncellemeyi Tüm Kayıtlı Kullanıcılara E-Posta Olarak Gönder"
                            className="w-8 h-8 rounded-lg hover:bg-amber-500/20 text-amber-400 border border-amber-500/30"
                          >
                            <Send className="w-3.5 h-3.5 inline" />
                          </button>
                        )}
                        {collection === 'blog_posts' && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`'${row.title || row.slug || row.id}' blog yazısını TÜM KAYITLI KULLANICILARA e-posta bildirimi olarak göndermek istiyor musunuz?`)) return;
                              try {
                                const res = await adminApi.sendBlogNotification(row.id);
                                toast({ title: 'Başarılı', description: res?.message || 'Blog bildirim e-postaları gönderildi.' });
                              } catch (err) {
                                toast({ title: 'Hata', description: err.response?.data?.detail || 'Mail gönderilemedi.', variant: 'destructive' });
                              }
                            }}
                            title="Bu Blog Yazısını Tüm Abonelere E-Posta Olarak Gönder"
                            className="w-8 h-8 rounded-lg hover:bg-[#e4b44e]/20 text-[#e4b44e] border border-[#e4b44e]/30"
                          >
                            <Send className="w-3.5 h-3.5 inline" />
                          </button>
                        )}
                        {(collection === 'email_templates' || collection === 'email_template_translations') && (
                          <button onClick={() => setPreviewTemplate(row)} title="E-posta Şablonunu Canlı Önizle" className="w-8 h-8 rounded-lg hover:bg-emerald-500/10 text-emerald-400">
                            <Eye className="w-3.5 h-3.5 inline" />
                          </button>
                        )}
                        <button onClick={() => openEdit(row)} className="w-8 h-8 rounded-lg hover:bg-white/5 text-[#f4d896]"><Edit3 className="w-3.5 h-3.5 inline" /></button>
                        <button onClick={() => remove(row)} className="w-8 h-8 rounded-lg hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length > 500 && <div className="p-3 text-xs text-white/40 text-center border-t border-white/5">İlk 500 kayıt gösteriliyor · arama yapın</div>}
        </div>
      )}

      {editItem !== null && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur z-50 flex items-center justify-center p-4" onClick={() => setEditItem(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#08080b] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">{creating ? 'Yeni Kayıt' : 'Düzenle'}</h2>
              <button onClick={() => setEditItem(null)} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              {Object.entries(editItem).map(([k, v]) => {
                if (k === '_id' || k === 'translations') return null;
                const isSystem = creating && (k === 'id' || k === 'created_at' || k === 'updated_at');
                if (isSystem) return null;
                return renderField(k, v, false);
              })}
            </div>
            
            {hasTranslations && editItem.translations && (
              <div className="mt-8 border-t border-white/10 pt-6">
                <div className="flex flex-wrap gap-2 border-b border-white/10 mb-6 pb-2">
                  {langs.map(l => (
                    <button type="button" key={l} onClick={() => setActiveLang(l)} className={`px-6 py-2.5 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors ${activeLang === l ? 'border-[#e4b44e] text-[#e4b44e]' : 'border-transparent text-white/40 hover:text-white'}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {langs.map(l => (
                  <div key={l} className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${l === activeLang ? 'block' : 'hidden'}`}>
                    {editItem.translations[l] && Object.entries(editItem.translations[l]).map(([k, v]) => {
                      if (k === 'id' || k.endsWith('_id') || k === 'language_code' || k === 'lang' || k === '_id') return null;
                      return renderField(k, v, true, l);
                    })}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-8">
              <button onClick={save} disabled={savingItem} className="btn-gold h-11 px-8 rounded-lg text-xs font-extrabold tracking-widest inline-flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> {savingItem ? 'KAYDEDİLİYOR...' : 'KAYDET'}
              </button>
              {(collection === 'email_templates' || collection === 'email_template_translations' || editItem.html || (editItem.translations && editItem.translations[activeLang]?.html)) && (
                <button type="button" onClick={() => setPreviewTemplate(editItem)} className="h-11 px-6 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 text-xs font-bold tracking-widest inline-flex items-center gap-2 transition">
                  <Eye className="w-4 h-4" /> CANLI ÖNİZLE
                </button>
              )}
              <button onClick={() => setEditItem(null)} className="h-11 px-8 rounded-lg border border-white/10 text-white/70 text-xs font-bold tracking-widest">İPTAL</button>
            </div>
          </div>
        </div>
      )}

      {previewTemplate && (
        <EmailPreviewModal template={previewTemplate} siteLogo={siteLogo} onClose={() => setPreviewTemplate(null)} />
      )}
    </div>
  );
}
